/**
 * Centralized role normalization.
 *
 * The database stores the org_role enum (`org_owner`, `admin`, `supervisor`,
 * `site_supervisor`, `dispatcher`, `officer`, `auditor`, `client_viewer`).
 * Navigation and UI code historically compared against display-ish strings
 * (`owner`, `guard`), which silently never matched. This module is the single
 * place that bridges the two — the database enum is NOT renamed.
 */

/** Role key used by navigation/UI code. */
export type NavRole =
  | "owner"
  | "admin"
  | "supervisor"
  | "site_supervisor"
  | "dispatcher"
  | "guard"
  | "auditor"
  | "client_viewer";

/** DB enum value → nav role. Only the two mismatched values are remapped. */
const DB_TO_NAV: Record<string, NavRole> = {
  org_owner: "owner",
  owner: "owner",
  admin: "admin",
  supervisor: "supervisor",
  site_supervisor: "site_supervisor",
  dispatcher: "dispatcher",
  officer: "guard",
  guard: "guard",
  contractor: "guard",
  auditor: "auditor",
  client_viewer: "client_viewer",
};

export function navRole(dbRole: string | null | undefined): NavRole {
  const key = (dbRole ?? "").toLowerCase();
  return DB_TO_NAV[key] ?? "guard";
}

/** True for a standard organization Guard (DB role `officer`). */
export function isGuard(dbRole: string | null | undefined): boolean {
  return navRole(dbRole) === "guard";
}

/** Human label for a DB role. */
export function roleLabel(dbRole: string | null | undefined): string {
  const map: Record<NavRole, string> = {
    owner: "Owner",
    admin: "Admin",
    supervisor: "Supervisor",
    site_supervisor: "Site Supervisor",
    dispatcher: "Dispatcher",
    guard: "Guard",
    auditor: "Auditor",
    client_viewer: "Client Viewer",
  };
  return map[navRole(dbRole)];
}

/**
 * Management surfaces a standard Guard must not reach — including by typing the
 * URL directly. UI visibility is not security; RLS remains authoritative, this
 * only stops guards from landing on management screens.
 */
export const GUARD_BLOCKED_PREFIXES = [
  "/admin",
  "/billing",
  "/invoicing",
  "/clients",
  "/team",
  "/contractors",
  "/manager",
  "/supervisor",
  "/operations/live",
  "/tracking",
  "/review",
  "/geofences",
  "/console/dispatch",
  "/exports",
  "/equipment-admin",
  "/vehicle-admin",
] as const;

export function isGuardBlockedPath(path: string): boolean {
  return GUARD_BLOCKED_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
}
